import { createContext, useContext, ReactNode } from "react";

import {
  useCollectionData,
  useDocumentData,
} from "react-firebase-hooks/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { collection, query, where, limit, doc } from "firebase/firestore";
import { firestore, auth } from "@/firebase";
import { companyConfigConverter, companyConverter } from "@/converters";
import {
  CompanyConfig,
  CompanySettings,
  CompanySettingsPrivate,
  CompanyType,
} from "@t3/shared";

type CompanyConfigContextType = {
  settings: CompanySettingsPrivate; //this will have ONLY the public settings for non-authenticated users
  publicSettings: CompanySettings;
  companyConfig: CompanyConfig | null;
  company: CompanyType; //yes, this can in theory be null, but only if the user is not authenticated, for simplicity we will state it as defined
};

const CompanyConfigContext = createContext<CompanyConfigContextType>(
  {} as CompanyConfigContextType,
);

export const useCompanyConfigContext = () => useContext(CompanyConfigContext);

export const CompanyConfigProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [authUser] = useAuthState(auth);

  const [companyConfigs, loading, error] = useCollectionData<CompanyConfig>(
    query(
      collection(firestore, "company-configs").withConverter(
        companyConfigConverter,
      ),
      where("status", "==", "active"),
      where("hostnames", "array-contains", window.location.host),
      limit(1),
    ),
  );

  const [companyDoc, companyLoading, companyError] =
    useDocumentData<CompanyType>(
      companyConfigs?.[0]?.id && authUser
        ? doc(firestore, "company", companyConfigs[0].companyId).withConverter(
            companyConverter,
          )
        : null,
    );
  if (error || companyError) {
    console.error(error, companyError);
    throw error || companyError;
  }

  if (loading || !companyConfigs?.[0]) {
    return <div>Loading...</div>;
  }

  // If user is authenticated, wait for company document to load
  if (authUser && (companyLoading || !companyDoc)) {
    return <div>Loading...</div>;
  }

  const companyConfig = companyConfigs[0];

  let settings: CompanySettingsPrivate;

  if (companyDoc) {
    if (companyDoc.settingsByConfig?.[companyConfig.id]) {
      settings = companyDoc.settingsByConfig[companyConfig.id];
    } else {
      if (companyDoc.settingsByConfig?.[companyDoc.defaultCompanyConfigId]) {
        settings =
          companyDoc.settingsByConfig[companyDoc.defaultCompanyConfigId];
      } else {
        settings = companyDoc.settings;
      }
    }
  } else {
    settings = companyConfig.settings as CompanySettingsPrivate;
  }

  const data = {
    settings,
    publicSettings: companyConfig.settings,
    companyConfig,
    company: companyDoc || (null as unknown as CompanyType),
  };

  return (
    <CompanyConfigContext.Provider value={data}>
      {children}
    </CompanyConfigContext.Provider>
  );
};
